import { Suspense, lazy, useState, useEffect, Fragment, useMemo, useRef } from 'react'
import { Box, Grid, IconButton, LinearProgress, Paper, Skeleton, Stack, Tooltip, Typography } from '@mui/material'
import { useSession } from 'components/Providers'
import { useDispatch, useSelector } from 'react-redux'
import { getWidgets, saveWidgetPerson } from 'store/widget'
import { useTranslation } from 'react-i18next'
import { CloseIcon, DragIndicatorIcon } from 'components/Icons'
import WidgetLibrary from 'components/WidgetLibrary'
import WidgetRenderer from 'components/Widgets/WidgetRenderer'
import ColumnMasonry from 'components/Widgets/ColumnMasonry'
import DashboardWelcome from './DashboardWelcome'
import Detail from 'pages/Academic/Student/ParentDetail'

const lazyComponents = new Map()

const componentFor = code => {
  if (!lazyComponents.has(code)) {
    lazyComponents.set(
      code,
      lazy(() => import(`components/Widgets/${code}`).catch(() => ({ default: () => <></> }))),
    )
  }
  return lazyComponents.get(code)
}

const preference = widget => widget.widgetPerson?.[0]
const isEnabled = widget => preference(widget)?.enabled !== false
const seqOf = widget => preference(widget)?.seq ?? 0
const isPinned = widget => widget.options?.pinned === true

export default function DashboardBody() {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const {
    person: { type, id },
    district,
  } = useSession()
  const widgets = useSelector(state => state.widget.list)
  const courseSessionList = useSelector(state => state.academic.courseSession.list)
  const [editing, setEditing] = useState(false)


  useEffect(() => {
    widgets || dispatch(getWidgets())
  }, [type, id, widgets])

  const hasNoData = code => code === 'Clients' && district.isServiceProvider === false

  const isTeacher = (courseSessionList?.length ?? 0) > 0

  const isForTeacher = widget => widget.options?.teacher !== true || type !== 'staff' || isTeacher

  const districtType = district?.type ?? (district?.isServiceProvider ? 'provider' : 'school')

  const isForDistrict = widget => {
    const audience = widget.options?.districtType

    if (!Array.isArray(audience) || !audience.length) return true

    return audience.includes(districtType)
  }

  const shown = useMemo(
    () =>
      (widgets ?? [])
        .filter(
          widget =>
            isEnabled(widget) && isForDistrict(widget) && isForTeacher(widget) && !hasNoData(widget.code),
        )
        .sort((a, b) => Number(isPinned(b)) - Number(isPinned(a)) || seqOf(a) - seqOf(b)),
    [widgets, district?.isServiceProvider, district?.type, isTeacher],
  )

  const pinned = useMemo(() => shown.filter(isPinned), [shown])
  const movable = useMemo(() => shown.filter(widget => !isPinned(widget)), [shown])

  const forDistrict = useMemo(
    () => (widgets ?? []).filter(widget => isForDistrict(widget) && isForTeacher(widget)),
    [widgets, districtType, isTeacher],
  )
  const available = forDistrict.filter(widget => !isEnabled(widget))
  const canCustomize = shown.length > 0 || available.length > 0

  const getLGSize = widget =>
    widget.options?.lg ?? (widget.code === 'PercentCard' || widget.code === 'GaugeCharts' ? 12 : 6)

  const setEnabled = (widget, enabled, seq) =>
    dispatch(
      saveWidgetPerson({
        personId: id,
        widgetId: widget.id,
        enabled,
        ...(seq === undefined ? {} : { seq }),
      }),
    )

  const toggle = (widget, enabled) => setEnabled(widget, enabled)

  const [dragging, setDragging] = useState(null)

  const [order, setOrder] = useState(null)
  const dropped = useRef(false)

  const list = useMemo(() => {
    if (!order) return shown
    const byId = new Map(movable.map(widget => [widget.id, widget]))
    const ordered = order.map(widgetId => byId.get(widgetId)).filter(Boolean)
    movable.forEach(widget => {
      if (!order.includes(widget.id)) ordered.push(widget)
    })
    return [...pinned, ...ordered]
  }, [shown, movable, pinned, order])

  const startDrag = (event, widget) => {
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', String(widget.id))
    dropped.current = false
    setDragging(widget.id)
    setOrder(movable.map(item => item.id))
  }

  const dragOverItem = targetId => {
    if (dragging === null || targetId === dragging) return
    setOrder(current => {
      if (!current) return current
      const from = current.indexOf(dragging)
      const to = current.indexOf(targetId)
      if (from < 0 || to < 0 || from === to) return current
      const next = [...current]
      next.splice(to, 0, next.splice(from, 1)[0])
      return next
    })
  }

  const commitOrder = async () => {
    dropped.current = true
    const finalOrder = order
    setDragging(null)

    if (!finalOrder || finalOrder.every((widgetId, index) => movable[index]?.id === widgetId)) {
      setOrder(null)
      return
    }

    const byId = new Map(movable.map(widget => [widget.id, widget]))
    await Promise.all(
      finalOrder.map((widgetId, index) => {
        const widget = byId.get(widgetId)
        return widget ? setEnabled(widget, true, index + 1) : null
      }),
    )
    setOrder(null)
  }

  const endDrag = () => {
    setDragging(null)
    if (!dropped.current) setOrder(null)
  }

  if (type === 'staff' && !courseSessionList) return <LinearProgress />

  return (
    <Fragment>
      <DashboardWelcome
        editing={editing}
        canCustomize={canCustomize}
        onToggle={() => setEditing(value => !value)}
      />

      {type === 'parent' && (
        <Box>
          <Detail />
        </Box>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={editing ? 8 : 12}>
          {!shown.length && (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderStyle: 'dashed', borderRadius: 2 }}>
              <Typography variant="subtitle1" color="text.secondary">
                {available.length ? t('Your dashboard is empty') : t('No widgets available for your account.')}
              </Typography>
              {available.length > 0 && (
                <Typography variant="body2" color="text.disabled">
                  {editing ? t('Add widgets from the library.') : t('Choose Customize to add widgets.')}
                </Typography>
              )}
            </Paper>
          )}
          <ColumnMasonry
            items={list}
            getKey={widget => widget.id}
            isFullWidth={widget => getLGSize(widget) === 12}
            columns={{ xs: 1, lg: editing ? 1 : 2, xl: editing ? 2 : 3 }}
            spacing={0.5}
            renderItem={widget => {
              const Component = widget.component ? null : componentFor(widget.code)
              const isDragging = dragging === widget.id
              return (
                  <Box
                    sx={{
                      position: 'relative',
                      borderRadius: 2,
                      border: '2px dashed',
                      borderColor: editing ? 'divider' : 'transparent',
                      transition: 'opacity 0.15s, border-color 0.15s',
                      ...(editing && {
                        cursor: !isPinned(widget) && movable.length > 1 ? 'move' : 'default',
                      }),
                      ...(isDragging && { opacity: 0.4, borderColor: 'primary.main' }),
                    }}
                    draggable={editing && movable.length > 1 && !isPinned(widget)}
                    onDragStart={event => startDrag(event, widget)}
                    onDragEnter={() => dragOverItem(widget.id)}
                    onDragOver={event => {
                      event.preventDefault()
                      event.dataTransfer.dropEffect = 'move'
                    }}
                    onDrop={event => {
                      event.preventDefault()
                      commitOrder()
                    }}
                    onDragEnd={endDrag}
                  >
    
                    <Box sx={{ ...(editing && { pointerEvents: 'none', opacity: 0.75 }) }}>
                      <Suspense fallback={<Skeleton variant="rounded" height={160} />} >
                        {Component ? <Component /> : <WidgetRenderer widget={widget} />}
                      </Suspense>
                    </Box>

                    {editing && (
                      <Stack
                        direction="row"
                        alignItems="center"
                        sx={{
                          position: 'absolute',
                          top: -12,
                          right: -12,
                          bgcolor: 'background.paper',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 5,
                          px: 0.5,
                          boxShadow: 1,
                        }}
                      >
                        {movable.length > 1 && !isPinned(widget) && (
                          <DragIndicatorIcon fontSize="small" sx={{ color: 'text.disabled', cursor: 'move' }} />
                        )}
                        <Tooltip
                          title={widget.required ? t('This widget is required and cannot be removed.') : t('Remove')}
                        >
                          <span>
                            <IconButton
                              size="small"
                              disabled={Boolean(widget.required)}
                              onClick={() => toggle(widget, false)}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    )}
                  </Box>
              )
            }}
          />
        </Grid>

        {editing && (
          <Grid item xs={12} md={4}>
            <WidgetLibrary
              widgets={forDistrict}
              isAdded={isEnabled}
              onAdd={widget => toggle(widget, true)}
              onClose={() => setEditing(false)}
            />
          </Grid>
        )}
      </Grid>
    </Fragment>
  )
}
